/**
 * Cross-workspace recommendation backlog — the "Needs recommendation" tab of
 * Site Health > Tickets. Lists, per workspace, the issues from the latest
 * completed crawl that have NO AI recommendation yet (~tens of thousands
 * globally: per-workspace counts carry the scale; a severity-ordered sample
 * capped server-side carries the actionable work).
 *
 * Every action is issueId-keyed so it works cross-workspace without tenant
 * plumbing (same contract as the tickets tab):
 *  - Generate      → queue background AI suggestion generation (drain writes
 *                    proposed_value; generation_status badge tracks progress)
 *  - File ticket   → escalate to a manual-fix ticket (moves to Tickets tab)
 *  - Open          → deep link into the owning workspace's audit view
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, Tooltip } from "antd";
import {
	ArrowUpRight,
	Building2,
	Inbox,
	LifeBuoy,
	Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type {
	RecommendationBacklogIssue,
	RecommendationBacklogWorkspace,
} from "@/types/technicalAudit";
import {
	useQueueRecommendationBacklog,
	useRecommendationBacklog,
	useRequestManualFix,
} from "@/hooks/useTechnicalAuditIssues";

const SEVERITY_CLS: Record<string, string> = {
	critical: "bg-destructive/10 text-destructive border-destructive/30",
	high: "bg-destructive/10 text-destructive border-destructive/30",
	medium: "bg-coral-light text-coral-dark border-coral/40",
	low: "bg-muted text-muted-foreground border-border",
};

const GENERATION_LABEL: Record<string, string> = {
	queued: "Queued…",
	generating: "Generating…",
	failed: "Generation failed",
};

function BacklogRow({
	issue,
	tenantId,
	runId,
}: {
	issue: RecommendationBacklogIssue;
	tenantId: string;
	runId: string;
}) {
	const navigate = useNavigate();
	const queueGeneration = useQueueRecommendationBacklog();
	const fileTicket = useRequestManualFix();
	// Optimistic "queued" so the row reacts before the 30s poll catches up.
	const [queuedLocally, setQueuedLocally] = useState(false);

	const generation = queuedLocally
		? "queued"
		: issue.generation_status ?? undefined;
	const inFlight = generation === "queued" || generation === "generating";

	return (
		<li className="flex flex-wrap items-center gap-3 px-4 py-2.5">
			<Badge
				variant="outline"
				className={cn("shrink-0", SEVERITY_CLS[issue.severity] ?? SEVERITY_CLS.low)}
			>
				{issue.severity}
			</Badge>
			<div className="min-w-0 flex-1">
				<p className="truncate font-mono text-sm text-foreground">
					{issue.page_url || "(no URL)"}
				</p>
				<p className="truncate text-xs text-muted-foreground">{issue.issue_type}</p>
			</div>
			{generation && (
				<span
					className={cn(
						"shrink-0 text-xs",
						generation === "failed" ? "text-destructive" : "text-teal-dark",
					)}
				>
					{GENERATION_LABEL[generation] ?? generation}
				</span>
			)}
			<Tooltip title="Queue AI suggestion generation for this issue">
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="h-7 shrink-0 gap-1 px-2 text-xs border-coral/40 text-coral-dark hover:bg-coral-light"
					disabled={inFlight || queueGeneration.isPending}
					onClick={() =>
						queueGeneration.mutate([issue.id], {
							onSuccess: () => setQueuedLocally(true),
						})
					}
				>
					<Sparkles className="h-3 w-3" />
					Generate
				</Button>
			</Tooltip>
			<Tooltip title="Escalate to a manual-fix ticket (shows on the Tickets tab)">
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="h-7 shrink-0 gap-1 px-2 text-xs"
					disabled={fileTicket.isPending}
					onClick={() =>
						fileTicket.mutate({
							issueId: issue.id,
							note: "Filed from the global recommendation backlog",
						})
					}
				>
					<LifeBuoy className="h-3 w-3" />
					File ticket
				</Button>
			</Tooltip>
			<Tooltip title="Open this issue type in the workspace's Technical Audit">
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="h-7 shrink-0 gap-1 px-2 text-xs"
					onClick={() =>
						navigate(
							`/audit/technical-issues?run_id=${runId}&type=${encodeURIComponent(issue.issue_type)}&tenant_id=${encodeURIComponent(tenantId)}`,
						)
					}
				>
					<ArrowUpRight className="h-3 w-3" />
					Open
				</Button>
			</Tooltip>
		</li>
	);
}

function BacklogWorkspaceSection({ ws }: { ws: RecommendationBacklogWorkspace }) {
	const queueGeneration = useQueueRecommendationBacklog();
	const sampled = ws.issues.filter(
		(i) => i.generation_status !== "queued" && i.generation_status !== "generating",
	);

	return (
		<div className="rounded-xl border bg-card shadow-sm">
			<div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
				<Building2 className="h-4 w-4 shrink-0 text-coral" />
				<span className="font-medium text-foreground">
					{ws.workspace_name || ws.tenant_id}
				</span>
				<span className="text-xs text-muted-foreground">{ws.tenant_id}</span>
				<span className="ml-2 flex flex-wrap gap-1">
					{ws.by_type.slice(0, 4).map((t) => (
						<span
							key={t.issue_type}
							className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground"
						>
							{t.issue_type} · {t.count}
						</span>
					))}
				</span>
				<span className="ml-auto flex items-center gap-2">
					<Badge variant="outline" className={SEVERITY_CLS.medium}>
						{ws.total.toLocaleString()} without recommendation
					</Badge>
					<Tooltip
						title={`Queue AI suggestion generation for the ${sampled.length} sampled issues below`}
					>
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="h-7 gap-1 px-2 text-xs border-coral/40 text-coral-dark hover:bg-coral-light"
							disabled={sampled.length === 0 || queueGeneration.isPending}
							onClick={() => queueGeneration.mutate(sampled.map((i) => i.id))}
						>
							<Sparkles className="h-3 w-3" />
							Generate sampled ({sampled.length})
						</Button>
					</Tooltip>
				</span>
			</div>
			<ul className="divide-y">
				{ws.issues.map((issue) => (
					<BacklogRow
						key={issue.id}
						issue={issue}
						tenantId={ws.tenant_id}
						runId={ws.run_id}
					/>
				))}
			</ul>
			{ws.total > ws.issues.length && (
				<p className="border-t px-4 py-2 text-xs text-muted-foreground">
					Showing the {ws.issues.length} highest-severity issues of{" "}
					{ws.total.toLocaleString()} — generate or resolve these and the next
					batch surfaces on refresh.
				</p>
			)}
		</div>
	);
}

export function RecommendationBacklogPanel({
	workspaceFilter,
}: {
	workspaceFilter: string;
}) {
	const { data, isLoading, error } = useRecommendationBacklog();

	if (isLoading) {
		return (
			<div className="flex h-96 items-center justify-center">
				<Spin size="large" />
			</div>
		);
	}
	if (error) {
		return (
			<div className="p-6 text-sm text-destructive">
				Failed to load the recommendation backlog.
			</div>
		);
	}

	const all = data?.workspaces ?? [];
	const workspaces =
		workspaceFilter === "all"
			? all
			: all.filter((w) => w.tenant_id === workspaceFilter);

	if ((data?.totals.issues ?? 0) === 0) {
		return (
			<div className="rounded-xl border bg-card px-8 py-16 text-center">
				<Inbox className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
				<h3 className="text-lg font-semibold text-foreground">
					Every issue has a recommendation
				</h3>
				<p className="mt-1 text-sm text-muted-foreground">
					Issues from your workspaces&apos; latest crawls that still need an AI
					recommendation will show up here.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{workspaces.map((ws) => (
				<BacklogWorkspaceSection key={ws.tenant_id} ws={ws} />
			))}
		</div>
	);
}

export default RecommendationBacklogPanel;
